import React from 'react';
import { Observe } from 'rewire-core';
import { WithStyle, withStyles } from 'rewire-ui';
import classNames from 'classnames';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import SaveIcon from '@material-ui/icons/SaveOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import UndoIcon from '@material-ui/icons/UndoOutlined';
import MessageIcon from '../ui/MessageIcon';
import { makeShortCellDate } from '../utility/DateUtils';
import { IMessage } from '../DataTypes';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import { deleteMessages, saveMessage } from '../timecard/MessagesModel';
import TextField from '@material-ui/core/TextField';
import { UTC } from 'rewire-common';

const styles = (theme: Theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flex: '0 1 auto',
    flexDirection: 'column',
  },
  messages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    '& .selected:not(:first-child)': {
      display: 'none',
    },
  },
  listItemIcon: {
    minWidth: '24px',
  },
  rightIcon: {
    right: '16px',
    color: '#5f727c',
  },
  leftIcon: {
    right: '36px',
    color: '#5f727c',
  },
  icon: {
    fontSize: '1.2em',
  },
  indicator: {
    display: 'flex',
    flexBasis: '10px',
    fontWeight: 'bold',
    fontSize: 18,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1em',
    padding: '3px 4px',
    minHeight: '36px',
    border: '1px solid #bbb',
    borderRadius: '4px',
    backgroundColor: '#fdffef',
  },
  messageClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f6f9e2',
    },
  },
  messageText: {
    padding: '0 36px 0 0',
    fontSize: '1em',
  },
  messageEdit: {
    padding: '0 36px 0 0',
    fontSize: '1em',
    flex: 1,
  },
  success: {
    // color: theme.palette.success.main,
    // borderColor: theme.palette.success.main,
  },
  warning: {
    // color: theme.palette.warning.main,
    // borderColor: theme.palette.warning.main,
  },
  error: {
    // color: theme.palette.error.main,
    // borderColor: theme.palette.error.main,
  },
  info: {
    // color: theme.palette.info.main,
    // borderColor: theme.palette.info.main,
  },
});

type IEffective = () => UTC | undefined;

interface IInfoPanelMessagesViewProps {
  messages: IMessage[];
  effective?: IEffective;
  className?: string;
  classes?: any;
  viewOnly?: boolean;

  onClick?: (message: IMessage) => void;
}

interface IInfoPanelMessageViewProps {
  messages: IMessage[];
  message: IMessage;
  effective?: IEffective;
  className?: string;
  classes?: any;
  viewOnly?: boolean;

  onClick?: (message: IMessage) => void;
}

function Indicator(props: { effective?: IEffective; message: IMessage; classes: any }) {
  return (
    <Observe>
      {() => {
        const selectedDate = props.effective ? props.effective() : undefined;
        const selected = selectedDate && selectedDate?.date === props.message.effective?.date;
        return (
          <div className={props.classes.indicator ?? ''}>
            <span className={selected ? 'selected' : ''}>{selected ? '»' : ''}</span>
          </div>
        );
      }}
    </Observe>
  );
}

type InfoPanelMessagesViewProps = WithStyle<ReturnType<typeof styles>, IInfoPanelMessagesViewProps>;

const InfoPanelMessageView = React.memo((props: IInfoPanelMessageViewProps) => {
  const { classes, message, onClick, viewOnly, effective } = props;
  const [saving, setSaving] = React.useState(false);
  const [editMode, _setEditMode] = React.useState(false);
  const [text, setText] = React.useState(message.text || '');

  const icon = <MessageIcon level={message.level} classes={{ icon: classes.icon }} />;
  let messageLevelClass = undefined;
  switch (message.level) {
    case 'success':
      messageLevelClass = classes.success;
      break;
    case 'warning':
      messageLevelClass = classes.warning;
      break;
    case 'error':
      messageLevelClass = classes.error;
      break;
    case 'info':
      messageLevelClass = classes.info;
  }
  const canEdit = !viewOnly && message.type === 'user';

  const setEditMode = React.useCallback(
    async (mode: boolean, save = false) => {
      if (!mode && save) {
        setSaving(true);
        if (
          !(await saveMessage(props.messages, {
            id: message.id,
            text: text,
          }))
        ) {
          setSaving(false);
          return;
        }
        setSaving(false);
      } else if (mode) {
        setText(message.text || '');
      }
      _setEditMode(mode);
    },
    [text],
  );

  const handleClick = React.useCallback(() => {
    if (onClick) {
      onClick(message);
    }
  }, []);

  const handleKeyDown = React.useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === 'Enter') {
        setEditMode(false, true);
      } else if (evt.key === 'Escape') {
        setEditMode(false);
      }
    },
    [text],
  );

  if (saving) {
    return <ListItemText className={classNames(classes.message, messageLevelClass)} primary="Saving..." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      {effective && <Indicator effective={effective} message={message} classes={classes} />}
      <ListItem
        key={message.id}
        className={classNames(classes.message, messageLevelClass, onClick ? classes.messageClickable : undefined)}
        onClick={handleClick}
      >
        {icon && <ListItemIcon className={classes.listItemIcon}>{icon}</ListItemIcon>}
        {editMode ? (
          <ListItem className={classes.messageEdit}>
            <TextField
              style={{ flex: 1 }}
              autoFocus
              variant="standard"
              placeholder="comment"
              value={text}
              onKeyDown={handleKeyDown}
              onChange={(v) => {
                setText(v.target.value);
              }}
            />
          </ListItem>
        ) : (
          <ListItemText
            className={classes.messageText}
            primary={
              <>
                {effective && <b>{makeShortCellDate(message.effective)}: </b>}
                {message.text}
              </>
            }
          />
        )}
        {!viewOnly &&
          (editMode ? (
            <>
              <ListItemSecondaryAction className={classes.leftIcon}>
                <IconButton edge="end" aria-label="edit" title={`Save ${text} message?`} onClick={() => setEditMode(false, true)}>
                  <SaveIcon />
                </IconButton>
              </ListItemSecondaryAction>
              <ListItemSecondaryAction className={classes.rightIcon}>
                <IconButton
                  edge="end"
                  aria-label="close"
                  title={`close without saving ${text} message?`}
                  onClick={() => setEditMode(false)}
                >
                  <UndoIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </>
          ) : (
            <>
              {canEdit && (
                <ListItemSecondaryAction className={classes.leftIcon}>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    title={`Edit ${message.level} message?`}
                    onClick={() => setEditMode(true)}
                  >
                    <EditIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              )}
              <ListItemSecondaryAction className={classes.rightIcon}>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  title={`Delete ${message.level} message?`}
                  onClick={() => deleteMessages(props.messages, message.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </>
          ))}
      </ListItem>
    </div>
  );
});

class InfoPanelMessagesView extends React.PureComponent<InfoPanelMessagesViewProps> {
  constructor(props: InfoPanelMessagesViewProps) {
    super(props);
  }

  render() {
    return (
      <Observe
        render={() => {
          const { classes, className, messages, onClick, viewOnly, effective } = this.props;
          return (
            <div className={classNames(className, classes.root)}>
              <List className={classes.messages}>
                {messages.map((message) => {
                  return (
                    <InfoPanelMessageView
                      key={message.id}
                      effective={effective}
                      message={message}
                      messages={messages}
                      classes={classes}
                      onClick={onClick}
                      viewOnly={viewOnly}
                    />
                  );
                })}
              </List>
            </div>
          );
        }}
      />
    );
  }
}

export default withStyles<IInfoPanelMessagesViewProps, unknown>(styles, InfoPanelMessagesView);
